import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// store
import { setBreadcrumbs } from '@/store/slices/breadcrumb';

// services
import { revenueCategoryService } from '@/services/revenue-category.service';

// utils
import { calculateChartData, getChartConfigs } from '@/utils/chartCalculations';

// components
import { Accordion } from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import { Button } from '@/components/ui/button';
// import RCItemAccordion from '@/components/molecules/RCItemAccordion';

// icons
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import infoIcon from '@/assets/icons/info.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import revenueIcon from '@/assets/icons/revenue.svg';

import rcExpanatoryImg from '@/assets/images/rc-explanatory-1.png';
import rcExpanatoryImg2 from '@/assets/images/rc-explanatory-2.png';
import rcExpanatoryImg3 from '@/assets/images/rc-explanatory-3.png';

import { PAGE } from '@/routes';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { useDeviceType } from '@/hooks/useDeviceType';
import RCItemAccordion from '@/components/molecules/RCItemAccordion';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';
import { Invoice } from '@/services/types/invoice.types';
import { invoiceService } from '@/services/invoice.service';
import TabPills from '@/components/molecules/TabPills';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';

const steps = [
  {
    title: 'What Are Revenue Categories?',
    message:
      'Organize your income streams with Revenue Categories. Easily allocate and track revenue from various sources.',
    img: rcExpanatoryImg
  },
  {
    title: 'Setting Revenue Targets (RCRT)',
    message:
      'Each Revenue Category has a specific Revenue Category Revenue Target (RCRT). Ensure your targets align with your overall budget goals.',
    img: rcExpanatoryImg2
  },
  {
    title: 'Linking Invoices to Revenue Categories',
    message:
      'Allocate revenue-related invoices to the appropriate Revenue Category for precise tracking and management.',
    img: rcExpanatoryImg3
  }
];

const RevenueCategoryPage = () => {
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const [revenueCategories, setRevenueCategories] = useState<any[]>([]);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('All');
  const [defaultRevenueCategory, setDefaultRevenueCategory] = useState<any | undefined>(undefined);

  const { budgetId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobileView } = useDeviceType();
  const { can } = useBudgetPermissions({ budgetId });
  const canCreateInvoice = can('invoice:create');
  const canCreateRevenueCategory = can('revenueCategory:create');
  const canEditRevenueCategory = can('revenueCategory:edit');

  const handleInvoiceSelect = (invoice: Invoice & { budget?: any }) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/invoices/${invoice._id}`, {
        state: { from: 'revenueCategory' }
      });
    }
  };

  const fetchRevenueCategories = async () => {
    if (!budgetId) return;

    try {
      setLoading(true);
      const { data } = await revenueCategoryService.getRevenueCategories(budgetId);
      setRevenueCategories(data || []);
      const defaultRevenueCategory = data.find((category: any) => category.is_default === true);
      if (defaultRevenueCategory) {
        setDefaultRevenueCategory(defaultRevenueCategory);
      }
    } catch (error) {
      console.error('Error fetching revenue categories:', error);
      setRevenueCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetInvoices = async () => {
    if (budgetId) {
      try {
        setLoadingInvoices(true);
        const { data: invoicesData } = await invoiceService.getInvoices({
          page: 1,
          limit: 12,
          budget_id: budgetId,
          invoice_category: 'REVENUE',
          type: undefined
        });
        const filteredInvoices =
          invoicesData.data && invoicesData.data.length > 0
            ? invoicesData.data.filter((invoice: any) => invoice.is_draft === false)
            : [];
        setInvoices(filteredInvoices || []);
      } catch (error) {
        console.error('Error fetching budget invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  console.log('revenueCategories', revenueCategories);

  useEffect(() => {
    fetchRevenueCategories();
  }, [budgetId]);

  useEffect(() => {
    fetchBudgetInvoices();
  }, [activeInvoiceTab, budgetId]);

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budget',
          path: `/budgets/${budgetId}`
        },
        {
          title: 'Revenue Categories Overview',
          path: ''
        }
      ])
    );
  }, [dispatch, budgetId]);

  const handleEditClick = () => {
    navigate(PAGE.REVENUE_CATEGORY.children.ADJUST);
  };

  const handleAddClick = () => {
    navigate(PAGE.REVENUE_CATEGORY.children.ADD, { state: { defaultRevenueCategory } });
  };

  // Calculate chart data for RCRT from revenue categories
  const rcrtRemaining = revenueCategories.reduce(
    (sum, category) => sum + (category.rcrt - category.rcrt_achieved || 0),
    0
  );
  const rcrtAchieved = revenueCategories.reduce((sum, category) => sum + (category.rcrt_achieved || 0), 0);
  const rcrtTotal = rcrtRemaining + rcrtAchieved;
  console.log('rcrtRemaining', rcrtRemaining);
  console.log('rcrtAchieved', rcrtAchieved);
  console.log('rcrtTotal', rcrtTotal);

  const rcrtChartData = [
    {
      key: 'target',
      value: rcrtTotal === 0 ? 100 : Math.round((rcrtRemaining / rcrtTotal) * 100),
      fill: '#32ADE6'
    },
    {
      key: 'achieved',
      value: rcrtTotal === 0 ? 0 : Math.round((rcrtAchieved / rcrtTotal) * 100),
      fill: '#48C80C'
    }
  ];

  const rcrtChartConfig = {
    target: { label: 'Revenue Target', color: '#32ADE6' },
    achieved: { label: 'Achieved Revenue', color: '#48C80C' }
  };

  return (
    <>
      <MobileAppBar
        title={<span className="capitalize">Revenue Category Overview</span>}
        onRightIconClick={() => {
          setCurrentStep(0);
          setIsOpen(true);
        }}
        rightIcon={infoIcon}
        rightTwoIcon={canEditRevenueCategory ? editIcon : undefined}
        onRightTwoIconClick={canEditRevenueCategory ? handleEditClick : undefined}
      />

      <div className="h-full w-full min-w-0 flex flex-col lg:flex-row p-4 lg:p-0 items-stretch lg:justify-between gap-4">
        {/* Main - Revenue Overview */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          <h2 className="text-base font-bold uppercase text-center mb-2 md:hidden">REVENUE CATEGORY</h2>
          <div className="md:border bg-white rounded-[16px] p-0 md:p-4">
            <header className="hidden md:flex items-start justify-between gap-4">
              <h2 className="text-2xl font-semibold uppercase">REVENUE CATEGORY</h2>
              <div className="flex flex-col items-end gap-3">
                <button
                  onClick={() => {
                    setCurrentStep(0);
                    setIsOpen(true);
                  }}
                >
                  <img className="w-[28px] h-[28px]" src={infoIcon} alt="info icon" />
                </button>

                <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                  <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                    <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                      <img
                        src={percentageIcon}
                        alt="percentage-icon"
                        width={13}
                        height={17}
                        className="group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                    <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                      <img
                        src={euroIcon}
                        alt="euro-icon"
                        width={12}
                        height={12}
                        className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </header>

            <div className="h-fit flex items-center justify-between gap-4 md:mt-3 md:border bg-white rounded-[16px] p-4 px-5">
              <div className="flex flex-col gap-1">
                <Tabs
                  className="md:hidden"
                  defaultValue="percentage"
                  value={graphViewMode}
                  onValueChange={setGraphViewMode}
                >
                  <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                    <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                      <img
                        src={percentageIcon}
                        alt="percentage-icon"
                        width={13}
                        height={17}
                        className="group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                    <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                      <img
                        src={euroIcon}
                        alt="euro-icon"
                        width={12}
                        height={12}
                        className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                      />
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                <p className="text-sm text-secondary">Achieved RCRT</p>
                <p className="text-[28px] font-bold leading-[24px] mb-2 md:mb-0">
                  {graphViewMode === 'percentage' ? `${rcrtChartData[1].value}%` : `€${rcrtAchieved.toLocaleString()}`}
                </p>
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <span className="w-[9px] h-[9px] rounded-full bg-[#32ADE6]"></span>
                    <p className="text-sm text-secondary">Revenue Target</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-[9px] h-[9px] rounded-full bg-[#48C80C]"></span>
                    <p className="text-sm text-secondary">Achieved Revenue</p>
                  </div>
                </div>
              </div>
              <div>
                <PieGraph
                  title="Achieved RCRT"
                  titleValue={
                    graphViewMode === 'percentage' ? `${rcrtChartData[1].value}%` : `€${rcrtAchieved.toLocaleString()}`
                  }
                  chartConfig={rcrtChartConfig}
                  chartData={rcrtChartData}
                  className=""
                  outerRadius={60}
                  minHeight={120}
                  hideLabel={true}
                />
              </div>
            </div>
          </div>

          <div className="hidden flex-1 min-w-0 md:flex flex-col border bg-white rounded-[16px] p-4">
            <header className="flex items-center justify-between gap-4">
              <h4 className="flex items-center gap-2">
                <img src={invoiceIcon} alt="invoice-icon" />
                <span className="font-bold">Recent Invoice</span>
              </h4>

              {invoices && invoices.length > 10 && (
                <Button
                  variant="text"
                  onClick={() => navigate(`/budgets/${budgetId}/invoices`)}
                  className="py-0 h-[30px]"
                >
                  See All
                </Button>
              )}
            </header>
            {invoices.length > 0 || activeInvoiceTab !== 'All' ? (
              <div className="flex flex-col gap-2 min-w-0 bg-lightPurple rounded-[16px] p-4 pt-2 mt-4 md:mt-3 md:p-0 md:bg-white">
                {/* <TabPills
                  options={['All', 'Expense', 'Revenue']}
                  value={activeInvoiceTab}
                  onValueChange={setActiveInvoiceTab}
                /> */}

                <InvoicesTable
                  invoices={invoices.slice(0, 10)}
                  setSelectedInvoice={() => {}}
                  onInvoiceSelect={handleInvoiceSelect}
                />
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                  <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                    <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                      There are no Invoice to show.
                    </span>{' '}
                    <span className="md:text-secondary md:text-base md:font-medium">
                      {' '}
                      Please create a new invoice to allocate budget!
                    </span>
                  </span>
                </div>
                {canCreateInvoice && (
                  <Button
                    onClick={() =>
                      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                        state: { from: 'budget', budgetId }
                      })
                    }
                    variant="primary-reverse"
                    className="rounded-full font-semibold px-5 text-base"
                  >
                    Create an Invoice
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - All Revenue Categories */}
        <div className="flex-1 min-w-0 w-full lg:flex-none lg:shrink-0 lg:max-w-[400px] my-4 md:pb-8 lg:my-0 lg:border lg:bg-white lg:rounded-[16px] lg:p-4 font-medium lg:font-bold text-xs lg:text-base lg:overflow-y-auto">
          <header className="flex items-center justify-between gap-5">
            <span className="text-xs md:text-sm">All Revenue {!isMobileView && 'Categories'}</span>

            {canEditRevenueCategory && (
              <button onClick={handleEditClick} title="Edit" className="hidden md:block">
                <img className="w-[28px] h-[28px]" src={editIcon} alt="edit icon" />
              </button>
            )}
          </header>

          <Accordion
            type="multiple"
            value={openItems}
            onValueChange={setOpenItems}
            className="flex flex-col gap-4 mt-5"
          >
            {loading ? (
              <p className="text-sm text-secondary text-center py-4 font-normal">Loading...</p>
            ) : revenueCategories && revenueCategories.length > 0 ? (
              revenueCategories.map((category: any) => (
                <RCItemAccordion
                  key={category._id}
                  rc={category}
                  graphViewMode={graphViewMode}
                  open={openItems.includes(category._id)}
                  onToggle={() => {
                    setOpenItems((prev) =>
                      prev.includes(category._id) ? prev.filter((id) => id !== category._id) : [...prev, category._id]
                    );
                  }}
                />
              ))
            ) : (
              <p className="text-sm text-secondary text-center py-4 font-normal">No revenue categories found</p>
            )}
          </Accordion>
        </div>

        {/* Recent Invoice (Mobile) */}
        <div className="flex-1 pb-8 md:hidden flex-col">
          <header className="mb-4">
            <h4 className="flex items-center gap-2">
              <span className="font-bold"> Invoice</span>
            </h4>
          </header>
          {invoices.length > 0 || activeInvoiceTab !== 'All' ? (
            <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 pt-2">
              {/* <TabPills
                options={['All', 'Expense', 'Revenue']}
                value={activeInvoiceTab}
                onValueChange={setActiveInvoiceTab}
              /> */}

              <InvoicesTable
                invoices={invoices.slice(0, 10)}
                setSelectedInvoice={() => {}}
                onInvoiceSelect={handleInvoiceSelect}
              />
            </div>
          ) : (
            <div className="flex-1 bg-lightPurple rounded-[16px] py-[1.5rem] px-[1rem] flex flex-col items-center justify-center gap-[1rem]">
              <img className="w-[80px] h-[80px]" src={noInvoiceIcon} alt="no-invoice-icon" />
              <p className="text-center font-medium text-xs w-[80%]">
                There are no Invoice to show. Please create a new invoice to allocate budget!
              </p>

              {canCreateInvoice && (
                <Button
                  onClick={() =>
                    navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                      state: { from: 'budget', budgetId }
                    })
                  }
                  variant={'primary-reverse'}
                  className="rounded-full px-6 w-fit text-base font-semibold"
                >
                  Create an Invoice
                </Button>
              )}
            </div>
          )}
        </div>

        {canCreateRevenueCategory && (
          <div className="rounded-[22px] flex-1 flex items-center justify-center absolute bottom-[26px] right-[36px] shadow-[0px_4px_4px_0px_#00000040]">
            <Button onClick={handleAddClick} className="px-6 w-[64px] h-[64px] rounded-[22px]">
              <span className="text-white text-[2.5rem] font-light">+</span>
            </Button>
          </div>
        )}
      </div>

      {/* Explanatory Modal */}
      {isOpen && (
        <ExplanatoryModal
          isOpen={isOpen}
          steps={steps}
          currentStep={currentStep}
          buttonText="Next"
          onButtonClick={() => {
            setCurrentStep(currentStep + 1);
          }}
          onClose={() => {
            setIsOpen(false);
          }}
          onStepChange={setCurrentStep}
        />
      )}
    </>
  );
};

export default RevenueCategoryPage;
